import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import dayjs from "dayjs";

Font.register({
  family: "Manrope",
  fonts: [
    { src: "/fonts/Manrope-Regular.ttf", fontWeight: "normal" },
    { src: "/fonts/Manrope-Bold.ttf", fontWeight: "bold" },
    { src: "/fonts/Manrope-Medium.ttf", fontWeight: "medium" },
  ],
});

// ✅ All columns defined in one place
const COL = {
  empId: 0.8,
  name: 1.6,
  jobTitle: 1.2,
  department: 1.0,
  division: 1.2,
  date: 1.2,
  inTime: 0.8,
  outTime: 0.8,
  normalHrs: 0.7,
  workedHrs: 0.7,
  otHrs: 0.5,
};

const styles = StyleSheet.create({
  page: {
    padding: 20,
    paddingBottom: 110,
    fontSize: 10,
    fontFamily: "Manrope",
  },
  headerSpace: {
    height: 80,
    marginBottom: 10,
  },
  header_image: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  centerDiv: {
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 10,
  },
  mainTitle: {
    fontSize: 13,
    fontWeight: "bold",
    marginBottom: 3,
  },
  smallText: {
    fontSize: 9,
    color: "grey",
    marginBottom: 6,
  },

  // ── Table ──────────────────────────────────
  tableHeader: {
    flexDirection: "row",
    paddingVertical: 6,
    paddingHorizontal: 6,
    backgroundColor: "#2663EB",
  },
  tableHeaderCell: {
    fontSize: 8,
    fontWeight: "bold",
    color: "#ffffff",
  },

  // Outer row per employee
  employeeRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
    alignItems: "flex-start",
  },

  // Common fields (left side)
  commonFields: {
    flexDirection: "column",
    justifyContent: "flex-start",
    paddingVertical: 4,
    paddingHorizontal: 6,
    // borderRightWidth: 1,
    borderRightColor: "#eee",
  },
  cell: {
    fontSize: 8,
    paddingVertical: 1,
  },
  cellBold: {
    fontSize: 8,
    fontWeight: "bold",
    paddingVertical: 1,
  },

  // Punch rows (right side)
  punchesColumn: {
    flex: 1,
    flexDirection: "column",
  },
  punchRow: {
    flexDirection: "row",
    // borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
    paddingVertical: 4,
    paddingHorizontal: 6,
  },
  punchCell: {
    fontSize: 8,
  },

  // ── Footer ─────────────────────────────────
  footerSpace: {
    position: "absolute",
    bottom: 22,
    left: 0,
    right: 0,
    height: 80,
  },
  footer_image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  footerPageNum: {
    position: "absolute",
    bottom: 6,
    left: 0,
    right: 0,
    textAlign: "center",
    fontSize: 9,
    color: "grey",
  },
});

const OvertimeReportDocument = ({ overtimeReportPrintData }) => {
  const bondTimeportNoData = JSON.parse(
    localStorage.getItem("BondTimeportNoData") || "[]",
  );

  const headerImage = bondTimeportNoData[0]?.REP_HEADER;
  const footerImage = bondTimeportNoData[0]?.REP_FOOTER;

  // ✅ Group by EMP ID — common fields once, punches array for repeated
  const groupedRows = Object.values(
    (overtimeReportPrintData || []).reduce((acc, r) => {
      const key = r["EMP ID"];
      if (!acc[key]) {
        acc[key] = { ...r, punches: [] };
      }
      acc[key].punches.push({
        DATE: r.DATE,
        IN_TIME: r.IN_TIME,
        OUT_TIME: r.OUT_TIME,
        NormalHrs: r.NormalHrs,
        WORKED_HOURS: r.WORKED_HOURS,
        OT_HOURS: r.OT_HOURS,
      });
      return acc;
    }, {}),
  );

  const fmt = (val, format) => {
    const d = dayjs(val);
    return d.isValid() ? d.format(format) : "-";
  };

  // Common flex total for left side
  const commonFlex =
    COL.empId + COL.name + COL.jobTitle + COL.department + COL.division;

  // Punch flex total for right side
  const punchFlex =
    COL.date +
    COL.inTime +
    COL.outTime +
    COL.normalHrs +
    COL.workedHrs +
    COL.otHrs;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* HEADER IMAGE */}
        <View style={styles.headerSpace} fixed>
          {headerImage && (
            <Image src={headerImage} style={styles.header_image} />
          )}
        </View>

        {/* TITLE */}
        <View style={styles.centerDiv}>
          <Text style={styles.mainTitle}>Overtime Report</Text>
          <Text style={styles.smallText}>
            Total: {overtimeReportPrintData?.length || 0} records
          </Text>
        </View>

        {/* TABLE HEADER */}
        <View style={styles.tableHeader}>
          {/* Common columns */}
          <View style={{ flex: COL.empId }}>
            <Text style={styles.tableHeaderCell}>Emp ID</Text>
          </View>
          <View style={{ flex: COL.name }}>
            <Text style={styles.tableHeaderCell}>Name</Text>
          </View>
          <View style={{ flex: COL.jobTitle }}>
            <Text style={styles.tableHeaderCell}>Job Title</Text>
          </View>
          <View style={{ flex: COL.department }}>
            <Text style={styles.tableHeaderCell}>Department</Text>
          </View>
          <View style={{ flex: COL.division }}>
            <Text style={styles.tableHeaderCell}>Division</Text>
          </View>
          {/* Punch columns */}
          <View style={{ flex: COL.date }}>
            <Text style={styles.tableHeaderCell}>Date</Text>
          </View>
          <View style={{ flex: COL.inTime }}>
            <Text style={styles.tableHeaderCell}>In Time</Text>
          </View>
          <View style={{ flex: COL.outTime }}>
            <Text style={styles.tableHeaderCell}>Out Time</Text>
          </View>
          <View style={{ flex: COL.normalHrs }}>
            <Text style={styles.tableHeaderCell}>Normal Hrs</Text>
          </View>
          <View style={{ flex: COL.workedHrs }}>
            <Text style={styles.tableHeaderCell}>Worked Hrs</Text>
          </View>
          <View style={{ flex: COL.otHrs }}>
            <Text style={styles.tableHeaderCell}>OT Hrs</Text>
          </View>
        </View>

        {/* TABLE ROWS */}
        {groupedRows.map((r, idx) => (
          <View key={idx} style={styles.employeeRow}>
            {/* ✅ Common fields — shown once per employee */}
            <View style={[styles.commonFields, { flex: commonFlex }]}>
              <View style={{ flexDirection: "row" }}>
                <View style={{ flex: COL.empId }}>
                  <Text style={styles.cell}>{r["EMP ID"] || "-"}</Text>
                </View>
                <View style={{ flex: COL.name }}>
                  <Text style={styles.cellBold}>{r.NAME || "-"}</Text>
                </View>
                <View style={{ flex: COL.jobTitle }}>
                  <Text style={styles.cell}>{r["JOB TITLE"] || "-"}</Text>
                </View>
                <View style={{ flex: COL.department }}>
                  <Text style={styles.cell}>{r.DEPARTMENT || "-"}</Text>
                </View>
                <View style={{ flex: COL.division }}>
                  <Text style={styles.cell}>{r.DIVISION || "-"}</Text>
                </View>
              </View>
            </View>

            {/* ✅ Punch rows — repeated per record */}
            <View style={[styles.punchesColumn, { flex: punchFlex }]}>
              {r.punches.map((punch, pIdx) => (
                <View
                  key={pIdx}
                  style={[
                    styles.punchRow,
                    // ✅ no border on last punch row
                    pIdx === r.punches.length - 1 && { borderBottomWidth: 0 },
                  ]}
                >
                  <View style={{ flex: COL.date }}>
                    <Text style={styles.punchCell}>
                      {fmt(punch.DATE, "YYYY-MM-DD")}
                    </Text>
                  </View>
                  <View style={{ flex: COL.inTime }}>
                    <Text style={styles.punchCell}>
                      {fmt(punch.IN_TIME, "HH:mm:ss")}
                    </Text>
                  </View>
                  <View style={{ flex: COL.outTime }}>
                    <Text style={styles.punchCell}>
                      {fmt(punch.OUT_TIME, "HH:mm:ss")}
                    </Text>
                  </View>
                  <View style={{ flex: COL.normalHrs }}>
                    <Text style={styles.punchCell} >
                      {punch.NormalHrs ?? "-"}
                    </Text>
                  </View>
                  <View style={{ flex: COL.workedHrs }}>
                    <Text style={styles.punchCell}>
                      {punch?.WORKED_HOURS != null
                        ? Number(punch.WORKED_HOURS).toFixed(2)
                        : "-"}
                    </Text>
                  </View>
                  <View style={{ flex: COL.otHrs }}>
                    <Text
                      style={[
                        styles.punchCell,
                        { color: punch.OT_HOURS > 0 ? "orange" : "#111" },
                      ]}
                    >
                     {punch?.OT_HOURS != null
                        ? Number(punch.OT_HOURS).toFixed(2)
                        : "-"}
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        ))}

        {/* FOOTER IMAGE */}
        <View style={styles.footerSpace} fixed>
          {footerImage && (
            <Image src={footerImage} style={styles.footer_image} />
          )}
        </View>

        {/* PAGE NUMBER */}
        <Text
          style={styles.footerPageNum}
          render={({ pageNumber, totalPages }) =>
            `Page ${pageNumber} of ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  );
};

export default OvertimeReportDocument;
